import { toLooseKey } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'
import { CashDraw } from '../cash-draw'
import type { DocumentNumber } from '../document-number'
import type { ProjectId } from '../project-id'

import { BudRef } from './bud-ref'
import type { Files } from './files'
import { LocRow } from './loc-row'

/**
 * The three BCH LOC Population source files, parsed so they can be joined by
 * project and by `COMBO2` instead of by Excel cell references.
 */
export interface SourceData {
  readonly glReport: SourceData.GlReport
  readonly awardType: SourceData.AwardType
  readonly priorCrosswalk: SourceData.PriorCrosswalk
}
export namespace SourceData {
  /** How one source file is read. */
  export interface Source {
    /** The sheet to read: `first` for a single-sheet export, else its name. */
    readonly sheet: string
    readonly cells: 'formatted' | 'native'
    /** Names the file in read errors. */
    readonly label: string
  }

  /**
   * How each source file is read.
   *
   * The general ledger report and award type query keep their native cell
   * types because they are copied straight through to the output workbook.
   * The prior cash draw is only used for lookups, so it is read as Excel
   * displays it, the same way the BCH justifications agent reads that
   * workbook (a `Document #` shown as `001234` stays `001234`).
   */
  export const SOURCES = {
    awardType: { cells: 'native', label: 'award type query', sheet: 'first' },
    glReport: {
      cells: 'native',
      label: 'general ledger report',
      sheet: 'first',
    },
    priorCashDraw: {
      cells: 'formatted',
      label: 'prior cash draw workbook',
      sheet: CashDraw.NAMES.loc,
    },
  } as const satisfies Record<keyof Files, Source>

  /** The worksheet rows read from each source file. */
  export interface Sheets {
    readonly glReport: readonly common.SheetRow[]
    readonly awardType: readonly common.SheetRow[]
    readonly priorCashDraw: readonly common.SheetRow[]
  }
  export namespace Sheets {
    export const isSheets: TypeChecker<Sheets> = TypeChecker.Factory.object({
      awardType: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
      glReport: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
      priorCashDraw: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
    }).usingAlias('SourceData.Sheets')
    export const assertIsSheets: TypeAsserter<Sheets> =
      TypeAsserter.Factory.from(isSheets)
  }

  /**
   * Builds the source data from each file's worksheet rows.
   */
  export function fromSheets(sheets: Readonly<Sheets>): SourceData {
    Sheets.assertIsSheets(sheets)
    return {
      awardType: AwardType.fromSheet(sheets.awardType),
      glReport: GlReport.fromSheet(sheets.glReport),
      priorCrosswalk: PriorCrosswalk.fromSheet(sheets.priorCashDraw),
    }
  }

  /**
   * The current period's `chglr008` general ledger extract: one row per
   * project/budget-year, with everything from `Long Description` onward carried
   * through to the output unchanged.
   */
  export interface GlReport {
    /** The sheet as read: the one source for the pass-through `chglr008` tab. */
    readonly rawRows: readonly common.SheetRow[]
    /** The data rows that have a `ProjectID`, parsed. */
    readonly rows: readonly GlReport.Row[]
    readonly layout: GlReport.Layout
  }
  export namespace GlReport {
    export interface Row {
      readonly setId: unknown
      readonly projectId: ProjectId
      /** As parsed by {@link BudRef.from}, for building `COMBO1` and `COMBO2`. */
      readonly budRef: BudRef | undefined
      /** The `Bud_ref` cell as read, which the `LOC` tab shows unchanged. */
      readonly budRefCell: unknown
      /** The columns after `Bud_ref`, verbatim and in their original order. */
      readonly carriedColumns: readonly unknown[]
    }

    /** Where the header row is, and where the carried columns start. */
    export interface Layout {
      readonly headerIndex: number
      readonly carriedFrom: number
    }

    /** A row from above or at the header, split where the carried columns start. */
    export interface SplitRow {
      readonly leading: common.SheetRow
      readonly carried: common.SheetRow
    }

    /** The output workbook's tab that copies this sheet through verbatim. */
    export const OUTPUT_TAB = 'chglr008'

    /**
     * Parses the `chglr008` worksheet. Its first three headers are the ones
     * the `LOC` tab keeps.
     */
    export function fromSheet(rows: readonly common.SheetRow[]): GlReport {
      const { body, columns, preamble } = common.SheetRow.parseTable(rows, {
        budRef: [LocRow.HEADERS.budRef],
        projectId: [LocRow.HEADERS.projectId],
        setId: [LocRow.HEADERS.setId],
      })
      const carriedFrom = columns.budRef + 1

      return {
        layout: { carriedFrom, headerIndex: preamble.length },
        rawRows: rows,
        rows: body.flatMap(row => {
          const projectId = common.SheetRow.cellText(row[columns.projectId])
          if (!projectId) {
            return []
          }
          return {
            budRef: BudRef.from(row[columns.budRef]),
            budRefCell: row[columns.budRef],
            carriedColumns: row.slice(carriedFrom),
            projectId,
            setId: row[columns.setId],
          }
        }),
      }
    }

    /**
     * The rows above the column header, then the header itself, each split
     * where the carried columns start. The `LOC` tab mirrors their layout.
     */
    export function headerRows(
      report: Readonly<GlReport>,
    ): readonly SplitRow[] {
      const { carriedFrom, headerIndex } = report.layout
      return report.rawRows.slice(0, headerIndex + 1).map(row => ({
        carried: row.slice(carriedFrom),
        leading: row.slice(0, carriedFrom),
      }))
    }
  }

  /**
   * The `CH_GM_AWARD_NUM_TYPE_PROJECT` query: each project's award type.
   */
  export interface AwardType {
    /** The sheet as read, for the pass-through `Query` tab. */
    readonly rawRows: readonly common.SheetRow[]
    /** Award type by the loose key of each `Project`. */
    readonly typeByProject: ReadonlyMap<ProjectId, string>
  }
  export namespace AwardType {
    /** The output workbook's tab that copies this sheet through verbatim. */
    export const OUTPUT_TAB = 'Query'

    /**
     * Parses the `CH_GM_AWARD_NUM_TYPE_PROJECT` worksheet.
     */
    export function fromSheet(rows: readonly common.SheetRow[]): AwardType {
      const { body, columns } = common.SheetRow.parseTable(rows, {
        awardType: ['Award Type'],
        project: ['Project'],
      })

      const typeByProject = new Map<ProjectId, string>()
      for (const row of body) {
        const projectId = common.SheetRow.cellText(row[columns.project])
        const awardType = common.SheetRow.cellText(row[columns.awardType])
        if (projectId && awardType) {
          typeByProject.set(toLooseKey(projectId), awardType)
        }
      }

      return { rawRows: rows, typeByProject }
    }
  }

  /**
   * The prior period's `LOC` tab, reduced to what this period needs from it:
   * `Account P or G` and `Document #` by `COMBO2`, and each project's most
   * recent award type for projects the current query has no type for.
   */
  export interface PriorCrosswalk {
    /** Award type by the loose key of each `ProjectID`. */
    readonly awardTypeByProject: ReadonlyMap<ProjectId, string>
    /** Keyed by the loose key of `COMBO2`, so formatting differences still match. */
    readonly entryByCombo2: ReadonlyMap<string, PriorCrosswalk.Entry>
  }
  export namespace PriorCrosswalk {
    export interface Entry {
      readonly accountPorG?: string
      readonly documentNumber?: DocumentNumber
    }

    /**
     * Parses the prior cash draw workbook's `LOC` worksheet, by the same
     * headers this period's `LOC` tab is written with.
     */
    export function fromSheet(
      rows: readonly common.SheetRow[],
    ): PriorCrosswalk {
      const { body, columns, header } = common.SheetRow.parseTable(rows, {
        accountPorG: [LocRow.HEADERS.accountPorG],
        combo2: [LocRow.HEADERS.combo2],
        documentNumber: [LocRow.HEADERS.documentNumber],
        projectId: [LocRow.HEADERS.projectId],
      })
      // Older workbooks may still call this column "Award Type" rather than
      // the current "LOC".
      const awardTypeColumn = common.SheetRow.findOptionalColumn(header, [
        LocRow.HEADERS.awardType,
        'Award Type',
      ])

      const entryByCombo2 = new Map<string, Entry>()
      const awardTypeByProject = new Map<ProjectId, string>()
      for (const row of body) {
        const combo2 = common.SheetRow.cellText(row[columns.combo2])
        if (combo2) {
          entryByCombo2.set(toLooseKey(combo2), {
            accountPorG: _filledIn(row[columns.accountPorG]),
            documentNumber: _filledIn(row[columns.documentNumber]),
          })
        }

        const projectId = common.SheetRow.cellText(row[columns.projectId])
        const awardType =
          awardTypeColumn === undefined
            ? undefined
            : common.SheetRow.cellText(row[awardTypeColumn])
        if (projectId && awardType) {
          awardTypeByProject.set(toLooseKey(projectId), awardType)
        }
      }

      return { awardTypeByProject, entryByCombo2 }
    }

    /**
     * A prior cell's text, or `undefined` when it is blank or shows
     * {@link LocRow.MISSING}: the value last period's writer rendered for a
     * missing one, and how the formatted read shows a real Excel `#N/A`.
     */
    function _filledIn(cell: unknown): string | undefined {
      const text = common.SheetRow.cellText(cell)
      return text?.toUpperCase() === LocRow.MISSING ? undefined : text
    }
  }
}
